import type { CSSProperties, FC } from 'react';

import ContainerForm from 'components/ContainerForm';
import { InfiniteComp } from 'components/infinite-scroll/InfiniteComp';
import { CustomTable } from 'components/table/style';
import { Badge, RadiusConfig, Row, Table, Typography } from 'enable-ui';

import {
  EmptyAddContentContainer,
  InfoContainer,
  TableContainer,
  TitleContainer,
} from 'modules/classes/styles/class';
import { AddItemModal } from 'modules/dashboard/components/AddItemModal';
import { CustomHeader } from 'modules/dashboard/components/CustomHeader';
import { BorderContainer } from 'modules/dashboard/styles/common';
import { TableHeader } from 'modules/dashboard/styles/dashboard';

import { ReactComponent as EmptyUserIcon } from 'assets/icons/Empty user.svg';
import { ReactComponent as BackIcon } from 'assets/icons/back.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/information.svg';
import { ReactComponent as SortIcon } from 'assets/icons/sort-default-12.svg';

import type { ClassesAddCompProps } from '../core/type';

import { isEmpty } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

export const ClassesAddComp: FC<ClassesAddCompProps> = ({
  trainersData,
  handleAddTrainersToClass,
  searchValue,
  onChangeSearchValue,
  columns,
  trainerColumns,
  selectedData,
  goBackToCreatePage,
  disabledButton,
  handleSubmit,
  hasMoreTrainer,
  handleLoadMoreTrainer,
}) => {
  const extraDropdownContentStyle: CSSProperties = !isEmpty(selectedData)
    ? { position: 'absolute', left: '-23rem', top: '-1rem' }
    : {};

  const renderContentModal = (title: string, isCustomButtonStyle?: boolean) => {
    return (
      <AddItemModal
        title={title}
        options={trainersData}
        onAdd={handleAddTrainersToClass}
        placeholderSearch="Search by name or email"
        searchValue={searchValue}
        onChange={onChangeSearchValue}
        content={
          <InfiniteComp hasMore={hasMoreTrainer} handleLoadMore={handleLoadMoreTrainer}>
            <Table
              columns={columns}
              dataSource={trainersData}
              total={99999}
              tableKey={''}
              queryState={{
                page: 1,
                limit: 99999,
              }}
              paginationConfigs={{
                show: false,
              }}
              headerConfigs={{
                show: false,
              }}
            />
          </InfiniteComp>
        }
        extraDropdownContentStyle={extraDropdownContentStyle}
        buttonStyle={
          isCustomButtonStyle
            ? {
                padding: '9px 16px',
                border: `1px solid ${COLOR.purpel['purpel-6']}`,
                borderRadius: RadiusConfig.sm,
              }
            : {}
        }
      />
    );
  };

  return (
    <ContainerForm
      buttonOrder="cancelFirst"
      onCancel={goBackToCreatePage}
      titleCancel={
        <Row className="gap-6">
          <BackIcon />
          <Typography variant="body-2" weight={600} color={COLOR.surfaceNeutralBold['neutral-11']}>
            Back
          </Typography>
        </Row>
      }
      onSubmit={handleSubmit}
      disabled={disabledButton}
      disabledCancel={false}
      titleSubmit="Add class"
    >
      <BorderContainer>
        <InfoContainer>
          <InfoIcon />
          <Typography variant="body-2" color={COLOR.purpel['purpel-6']}>
            A class must have at least one non-volunteer trainer.
          </Typography>
        </InfoContainer>

        <TitleContainer>
          <Typography
            className="flex gap-6 items-center"
            variant="h4"
            weight={500}
            color={COLOR.surfaceNeutralBold['neutral-11']}
          >
            Trainers
            <Badge
              color={COLOR_CONFIG.white}
              count={selectedData.length}
              bg={COLOR_CONFIG['neutral-text']}
              style={{
                padding: '2px 6px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 'calc(16/12)',
              }}
            />
          </Typography>

          {!isEmpty(selectedData) && (
            <div className="button-container">{renderContentModal('Add trainer', true)}</div>
          )}
        </TitleContainer>

        {isEmpty(selectedData) && (
          <>
            <TableHeader style={{ padding: '14px 16px', marginTop: 16 }}>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexGrow: 1,
                }}
              >
                <CustomHeader id="name" title="Name" />
                <SortIcon />
              </div>
              <CustomHeader title="Role" id="role" styleHeader={{ minWidth: 260 }} />
            </TableHeader>

            <EmptyAddContentContainer className="flex items-center justify-center flex-col mt-24">
              <EmptyUserIcon />
              <Typography className="mb-24" variant="body-3" color={COLOR_CONFIG['neutral-text']}>
                No trainer was added yet.
              </Typography>
              <div className="button-container mb-16">
                {renderContentModal('Add trainer', true)}
              </div>
            </EmptyAddContentContainer>
          </>
        )}

        <>
          {!isEmpty(selectedData) && (
            <TableContainer>
              <CustomTable style={{ border: 'none', padding: '16px 0' }}>
                <Table
                  columns={trainerColumns}
                  dataSource={selectedData}
                  total={99999}
                  tableKey={''}
                  queryState={{
                    page: 1,
                    limit: 99999,
                  }}
                  paginationConfigs={{
                    show: false,
                  }}
                />
              </CustomTable>
            </TableContainer>
          )}
        </>
      </BorderContainer>
    </ContainerForm>
  );
};
